import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, Subscription, interval, takeUntil, finalize, catchError, EMPTY, firstValueFrom } from 'rxjs';

import { AmplifyService } from 'src/@ccmc/services/amplify.service';
import { CcmcApiService } from 'src/@ccmc/services/ccmc-api.service';
import { SnackbarService } from 'src/@ccmc/services/snackbar.service';
import { AssetService } from 'src/@ccmc/services/asset.service';
import {
  DeliveryLog,
  NotificationService,
  NotificationTimeZoneOption
} from 'src/@ccmc/services/notification.service';
import { SpinnerService } from 'src/@ccmc/services/spinner.service';

/**
 * The tabs this dialog can show. Admins get all three; everyone else gets the
 * schedule and delivery logs, so the footer is driven by the active tab rather
 * than a tab index that differs between the two layouts.
 */
type NotificationTab = 'schedule' | 'manageUsers' | 'deliveryLogs';

/** Maps EventBridge cron day-of-week tokens to JavaScript day indexes. */
const CRON_DAY_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6
};

/** Maps the stored subscription weekDay values to JavaScript day indexes. */
const WEEK_DAY_INDEX: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
};

/**
 * Human labels for the report windows NotificationService.buildSchedule emits,
 * used by the schedule summary's "Data window" row.
 */
const REPORT_WINDOW_LABELS: Record<string, string> = {
  '1d': 'Last 24 hours',
  '7d': 'Last 7 days',
  '14d': 'Last 14 days'
};

/** Shown in the summary when the user has not chosen a frequency yet. */
const NOT_CONFIGURED_LABEL = 'Not configured';

/** The only product these notifications cover today. */
const DEFAULT_PRODUCT_LABEL = 'QuickBridge';

/** Summary wording for the event-driven (non-scheduled) cadence. */
const INSTANT_SCHEDULE_LABEL = 'Instant (after each loan)';

const DAYS_PER_WEEK = 7;
const HOURS_PER_MERIDIEM_CYCLE = 12;

/** How often delivery logs are re-fetched while a scheduled run is landing. */
const LOG_POLL_INTERVAL_MS = 2_000;
/** How long polling stays active once started. */
const LOG_POLL_DURATION_MS = 6 * 60 * 1000;
/** Furthest ahead a scheduled run can be for polling to be armed in advance. */
const LOG_POLL_MAX_WAIT_MS = 10 * 60 * 1000;
/** Upper bound on the day-by-day search for the next cron occurrence. */
const NEXT_RUN_SEARCH_DAYS = 35;

/**
 * Dialog component for managing QuickBridge notification subscriptions.
 *
 * Allows the current user to configure their own notification preferences
 * (email recipients, schedule, active/inactive toggle). Admin users can
 * also manage notification settings on behalf of other users in the same
 * account via a secondary "managed user" view, and review the email delivery
 * history for their own subscriptions on the delivery logs tab.
 */
@Component({
  selector: 'app-quickbridge-notification-dialog',
  templateUrl: './quickbridge-notification-dialog.component.html',
  styleUrls: ['./quickbridge-notification-dialog.component.scss']
})
export class QuickBridgeDialogComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  /** Emits on destroy to cancel all in-flight subscriptions. */
  private readonly unsubscribe = new Subject<void>();

  /** Columns rendered in the user management table (admin view). */
  readonly displayedColumns = ['select', 'email'];

  /**
   * Options for the schedule form's Time Zone dropdown. A daylight-saving zone
   * appears twice, once per abbreviation, each a fixed offset: "(AEST) Sydney"
   * is UTC+10 and "(AEDT) Sydney" is UTC+11, whatever the season.
   */
  readonly timeZones: NotificationTimeZoneOption[] = this.notificationService.getTimeZoneOptions();

  /** Tab order for the admin layout, indexed by mat-tab position. */
  private readonly adminTabs: NotificationTab[] = ['schedule', 'manageUsers', 'deliveryLogs'];
  /** Tab order for the non-admin layout, indexed by mat-tab position. */
  private readonly standardTabs: NotificationTab[] = ['schedule', 'deliveryLogs'];

  /** Which tab is currently open, in either layout. */
  activeTab: NotificationTab = 'schedule';

  // --- UI state flags ---
  showSpinner = false;
  isAdmin = false;
  isUserNXTsoft = false;
  isAdminManagingUser = false;
  pleaseWait = false;
  /**
   * True while the schedule tab shows the editable form. The tab opens on a
   * read-only summary; the edit button swaps in the form.
   */
  isEditingSchedule = false;
  formDirty = false;
  managedFormDirty = false;
  subscriptionFlag = false;
  managedSubscriptionFlag = false;

  // --- Delivery logs tab ---
  deliveryLogs: DeliveryLog[] = [];
  isLoadingLogs = false;
  /** True while logs are being polled around a scheduled run. */
  autoRefreshActive = false;
  private autoRefreshSub: Subscription | null = null;
  private autoRefreshStartTimer: ReturnType<typeof setTimeout> | null = null;
  private autoRefreshStopTimer: ReturnType<typeof setTimeout> | null = null;

  dataSource = new MatTableDataSource<any>([]);
  selectedRows = new Set<any>();
  savedRows = new Set<any>();

  // --- Email fields bound to the form inputs ---
  primaryEmail: string;
  managedPrimaryEmail: string;

  /** Current user's notification subscription config. */
  subscription = this.buildEmptySubscription();
  /** Managed user's notification subscription config (admin view). */
  managedSubscription = this.buildEmptySubscription();

  /**
   * Copy of the subscription taken when the schedule edit opened, so cancelling
   * discards edits. The form binds straight to the model, so without a snapshot
   * there is nothing to restore from.
   */
  private scheduleEditSnapshot: any = null;

  /** The row object of the user currently being managed by the admin. */
  private managedRow: any = null;
  private users: any;
  private assetUsers: any;

  constructor(
    private dialogRef: MatDialogRef<QuickBridgeDialogComponent>,
    private snackBarService: SnackbarService,
    private ccmcApiService: CcmcApiService,
    private amplifyService: AmplifyService,
    private assetService: AssetService,
    private notificationService: NotificationService,
    private spinnerService: SpinnerService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    // Subscribe to global spinner state so this dialog can show its own indicator.
    this.spinnerService.spinner
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(spinner => {
        this.showSpinner = spinner;
      });

    this.spinnerService.setShowSpinner(true);
    console.log('opened notification dialog');
    await this.loadInitialData();

    // Reset shared user state to avoid stale data from previous sessions.
    this.ccmcApiService.setUsers(undefined);
    this.getIfNxtsoft();

    // External (non-NXTsoft) users see a list of account users they can manage.
    if (!this.isUserNXTsoft && this.isAdmin) {
      this.pleaseWait = true;
      await this.getExternalView();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // --- Delivery logs tab ---

  /** Handles tab changes in the admin layout. */
  onAdminTabChange(tabIndex: number): void {
    this.openTab(this.adminTabs[tabIndex]);
  }

  /** Handles tab changes in the non-admin layout. */
  onStandardTabChange(tabIndex: number): void {
    this.openTab(this.standardTabs[tabIndex]);
  }

  /** True while the delivery logs tab is open, in either layout. */
  get isViewingDeliveryLogs(): boolean {
    return this.activeTab === 'deliveryLogs';
  }

  /**
   * Activates a tab. Logs are fetched lazily the first time the delivery logs
   * tab is opened, and polling is torn down as soon as the user leaves it.
   */
  private openTab(tab: NotificationTab): void {
    this.activeTab = tab;

    if (tab !== 'deliveryLogs') {
      this.stopAutoRefresh();
      return;
    }

    if (this.deliveryLogs.length === 0) {
      this.loadLogs();
    }
    this.evaluateAutoRefresh();
  }

  /**
   * Loads delivery logs for the current user's subscriptions.
   *
   * Silent loads are used by the poller: they skip the loading indicator and
   * suppress error toasts so a transient failure mid-poll is not surfaced as a
   * user-facing error. The array is always replaced so status transitions
   * (PENDING to SENT) are picked up.
   */
  loadLogs(silent = false): void {
    if (!silent) {
      this.isLoadingLogs = true;
    }

    this.notificationService
      .listLogs(this.assetService.getSelectedAssetId(), this.amplifyService.getCognitoId())
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: logs => {
          this.deliveryLogs = logs || [];
          this.isLoadingLogs = false;
        },
        error: err => {
          console.error('Failed to load delivery logs', err);
          if (!silent) {
            this.snackBarService.openSnackBar('Failed to load delivery logs.', 'Okay');
          }
          this.isLoadingLogs = false;
        }
      });
  }

  /** Maps a delivery status to its badge style class. */
  getStatusClass(status: string): string {
    switch (status) {
      case 'SENT':
        return 'status-sent';
      case 'FAILED':
        return 'status-failed';
      case 'PENDING':
        return 'status-pending';
      default:
        return '';
    }
  }

  /**
   * Arms log polling around the next scheduled run so a newly delivered email
   * shows up without the user refreshing.
   *
   * If the run is already inside the polling window, polling starts now. If it
   * is further out but within LOG_POLL_MAX_WAIT_MS, polling is deferred until
   * the run time. Anything further away is left to the manual refresh button.
   */
  private evaluateAutoRefresh(): void {
    this.stopAutoRefresh();

    const nextRun = this.getNextRun(this.subscription);
    if (!nextRun) {
      return;
    }

    const msUntilRun = nextRun.getTime() - Date.now();
    if (msUntilRun <= 0) {
      return;
    }

    if (msUntilRun <= LOG_POLL_DURATION_MS) {
      this.startPolling();
      return;
    }

    if (msUntilRun <= LOG_POLL_MAX_WAIT_MS) {
      this.autoRefreshStartTimer = setTimeout(() => {
        this.autoRefreshStartTimer = null;
        if (this.isViewingDeliveryLogs) {
          this.startPolling();
        }
      }, msUntilRun);
    }
  }

  private startPolling(): void {
    this.autoRefreshActive = true;

    this.autoRefreshSub = interval(LOG_POLL_INTERVAL_MS)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (this.isViewingDeliveryLogs) {
          this.loadLogs(true);
        }
      });

    this.autoRefreshStopTimer = setTimeout(() => this.stopAutoRefresh(), LOG_POLL_DURATION_MS);
  }

  private stopAutoRefresh(): void {
    this.autoRefreshActive = false;

    this.autoRefreshSub?.unsubscribe();
    this.autoRefreshSub = null;

    if (this.autoRefreshStartTimer) {
      clearTimeout(this.autoRefreshStartTimer);
      this.autoRefreshStartTimer = null;
    }
    if (this.autoRefreshStopTimer) {
      clearTimeout(this.autoRefreshStopTimer);
      this.autoRefreshStopTimer = null;
    }
  }

  /**
   * Resolves the next occurrence of a subscription's UTC cron expression.
   *
   * Returns null when the subscription has no usable schedule: instant
   * (post-loan) notifications, inactive subscriptions, and expressions with
   * wildcard minutes or hours have no single next run to compute.
   */
  private getNextRun(subscription: any): Date | null {
    if (!subscription?.isActive || !subscription.schedule || subscription.notificationType === 'post-loan') {
      return null;
    }

    const match = subscription.schedule.match(/^cron\((\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\)$/);
    if (!match) {
      return null;
    }
    const [, cronMinute, cronHour, cronDayOfMonth, , cronDayOfWeek] = match;

    const utcMinute = parseInt(cronMinute, 10);
    const utcHour = parseInt(cronHour, 10);
    if (isNaN(utcMinute) || isNaN(utcHour)) {
      return null;
    }

    const now = new Date();
    for (let dayOffset = 0; dayOffset < NEXT_RUN_SEARCH_DAYS; dayOffset++) {
      const candidate = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + dayOffset,
        utcHour,
        utcMinute,
        0,
        0
      ));

      if (candidate.getTime() <= now.getTime()) {
        continue;
      }
      if (!this.matchesCronDayOfWeek(cronDayOfWeek, candidate.getUTCDay())) {
        continue;
      }
      if (!this.matchesCronDayOfMonth(cronDayOfMonth, candidate.getUTCDate())) {
        continue;
      }
      return candidate;
    }

    return null;
  }

  private matchesCronDayOfWeek(cronDayOfWeek: string, utcDay: number): boolean {
    if (cronDayOfWeek === '?' || cronDayOfWeek === '*') {
      return true;
    }
    if (cronDayOfWeek === 'MON-FRI') {
      return utcDay !== 0 && utcDay !== 6;
    }
    const allowedDays = cronDayOfWeek
      .split(',')
      .map(day => CRON_DAY_INDEX[day.trim()])
      .filter(day => day !== undefined);
    return allowedDays.length === 0 || allowedDays.includes(utcDay);
  }

  private matchesCronDayOfMonth(cronDayOfMonth: string, utcDate: number): boolean {
    if (cronDayOfMonth === '*' || cronDayOfMonth === '?') {
      return true;
    }
    return utcDate === parseInt(cronDayOfMonth, 10);
  }

  // --- Next notification date ---

  // --- Schedule summary ---

  /** Product name shown on the summary card, defaulted for older configs. */
  get productLabel(): string {
    return this.subscription.product || DEFAULT_PRODUCT_LABEL;
  }

  /**
   * True when the summary should badge the cadence. The badge states what the
   * user is currently receiving, so it is hidden for a schedule that is only
   * configured: either no frequency chosen, or opted out.
   */
  get isScheduleBadgeVisible(): boolean {
    return !!this.subscription.timeInterval && !!this.subscription.isActive;
  }

  /** Opens the schedule form, keeping a snapshot so the edit can be cancelled. */
  startScheduleEdit(): void {
    this.scheduleEditSnapshot = JSON.parse(JSON.stringify(this.subscription));
    this.isEditingSchedule = true;
  }

  /** Discards unsaved schedule edits and returns to the summary. */
  cancelScheduleEdit(): void {
    if (this.scheduleEditSnapshot) {
      this.subscription = this.scheduleEditSnapshot;
    }
    this.closeScheduleEdit();
  }

  /**
   * Describes a subscription's cadence for the summary, for example
   * "Every Monday at 1:00 PM".
   *
   * Built from the bound form model rather than the stored cron expression so
   * the summary reflects what was last saved from this dialog: saveSubscription
   * derives the cron from a clone and never writes it back to the model.
   */
  getScheduleSummary(subscription: any): string {
    if (!subscription?.timeInterval) {
      return NOT_CONFIGURED_LABEL;
    }
    if (subscription.timeInterval === 'instantly') {
      return INSTANT_SCHEDULE_LABEL;
    }

    const timeOfDay = this.formatTimeOfDay(subscription.time);
    if (!timeOfDay) {
      return NOT_CONFIGURED_LABEL;
    }
    if (subscription.timeInterval === 'daily') {
      return `Every day at ${timeOfDay}`;
    }

    const weekDayLabel = this.formatWeekDay(subscription.weekDay);
    if (!weekDayLabel) {
      return NOT_CONFIGURED_LABEL;
    }
    return subscription.timeInterval === 'bi-weekly'
      ? `Every other ${weekDayLabel} at ${timeOfDay}`
      : `Every ${weekDayLabel} at ${timeOfDay}`;
  }

  /**
   * Returns the label for how much history a report covers, or null when the
   * cadence has no report window (instant sends and unconfigured schedules).
   *
   * The window is resolved through buildSchedule so the summary and the saved
   * config always agree on it.
   */
  getReportWindowLabel(subscription: any): string | null {
    if (!subscription?.timeInterval || subscription.timeInterval === 'instantly') {
      return null;
    }

    const { reportWindow } = this.notificationService.buildSchedule(
      subscription.time,
      subscription.timeInterval,
      subscription.weekDay,
      this.getSubscriptionTimeZone(subscription)
    );
    return REPORT_WINDOW_LABELS[reportWindow] || reportWindow;
  }

  /**
   * Returns the label of a subscription's time zone, or null when no cadence has
   * been chosen yet.
   *
   * Instant sends are included: the zone is captured for every cadence, so the
   * summary reports it even when there is no send time to qualify.
   *
   * An unrecognised stored identifier is shown as-is rather than hidden, so a
   * zone retired from the dropdown is still visible to the user.
   */
  getTimeZoneLabel(subscription: any): string | null {
    if (!subscription?.timeInterval) {
      return null;
    }

    const selectedTimeZone = this.findTimeZoneOption(subscription);
    return selectedTimeZone ? selectedTimeZone.label : subscription.timeZone || null;
  }

  /**
   * The next scheduled run rendered in the subscription's own time zone, with
   * the selected option's abbreviation, for example
   * "Oct 14, 2026 2:22 AM (AEST)" when "(AEST) Sydney, Melbourne" is chosen.
   *
   * Reported in the schedule's zone rather than the viewer's so it agrees with
   * the Schedule row above it. Returns null when there is no scheduled send,
   * which keeps the row hidden for instant and unconfigured cadences.
   */
  getNextRunDisplay(subscription: any): string | null {
    const nextRun = this.getNextScheduledRun(subscription);
    if (!nextRun) {
      return null;
    }

    const timeZone = this.getSubscriptionTimeZone(subscription);
    const formattedRun = this.formatInstantInZone(nextRun, timeZone);
    // The selected option's abbreviation, since the time is pinned to that
    // offset. Falls back to a date lookup for a zone that isn't offered.
    const abbreviation =
      this.findTimeZoneOption(subscription)?.abbreviation ??
      this.notificationService.getTimeZoneAbbreviation(subscription?.timeZone, nextRun);

    return abbreviation ? `${formattedRun} (${abbreviation})` : formattedRun;
  }

  /**
   * Formats an instant as "Sep 23, 2026 2:00 PM" in the given zone.
   *
   * Assembled from individual fields rather than a locale format string so the
   * layout stays fixed regardless of browser locale. An unrecognised zone falls
   * back to the browser's own.
   */
  private formatInstantInZone(instant: Date, timeZone: string): string {
    const fieldOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    let formattedParts: Intl.DateTimeFormatPart[];
    try {
      formattedParts = new Intl.DateTimeFormat('en-US', {
        ...fieldOptions,
        timeZone
      }).formatToParts(instant);
    } catch {
      formattedParts = new Intl.DateTimeFormat('en-US', fieldOptions).formatToParts(instant);
    }

    const field = (type: string) =>
      formattedParts.find(part => part.type === type)?.value ?? '';

    return (
      `${field('month')} ${field('day')}, ${field('year')} ` +
      `${field('hour')}:${field('minute')} ${field('dayPeriod')}`
    );
  }

  /** Formats a local "HH:mm" value as "1:05 PM", or null when unparsable. */
  private formatTimeOfDay(time: string): string | null {
    const parsedTime = /^(\d{1,2}):(\d{2})$/.exec(time || '');
    if (!parsedTime) {
      return null;
    }

    const hour24 = Number(parsedTime[1]);
    const meridiem = hour24 < HOURS_PER_MERIDIEM_CYCLE ? 'AM' : 'PM';
    // Midnight and noon both map to 12 in a 12-hour clock.
    const hour12 = hour24 % HOURS_PER_MERIDIEM_CYCLE || HOURS_PER_MERIDIEM_CYCLE;
    return `${hour12}:${parsedTime[2]} ${meridiem}`;
  }

  /** Capitalizes a stored weekDay value, or null when it is not a weekday. */
  private formatWeekDay(weekDay: string): string | null {
    const storedWeekDay = (weekDay || '').toLowerCase();
    if (WEEK_DAY_INDEX[storedWeekDay] === undefined) {
      return null;
    }
    return storedWeekDay.charAt(0).toUpperCase() + storedWeekDay.slice(1);
  }

  /**
   * Returns the label of the schedule field a subscription still needs before it
   * can be saved or activated, or null when the schedule is complete.
   *
   * A weekday only applies to the cadences that pin the send to one day of the
   * week; daily and instant sends ignore it.
   */
  private findMissingScheduleField(subscription: any): string | null {
    if (!subscription.timeInterval) {
      return 'Frequency';
    }

    const needsWeekDay =
      subscription.timeInterval === 'weekly' || subscription.timeInterval === 'bi-weekly';
    if (needsWeekDay && !subscription.weekDay) {
      return 'Weekday';
    }

    return null;
  }

  /** Leaves the schedule form without touching the subscription. */
  private closeScheduleEdit(): void {
    this.scheduleEditSnapshot = null;
    this.formDirty = false;
    this.isEditingSchedule = false;
  }

  // --- Next notification date ---

  /**
   * Resolves the instant at which the user will next receive a notification for
   * the given subscription, or null when there is no next send.
   *
   * Takes a bound form model, whose `time` is in the user's local zone, and
   * converts it the same way saveSubscription does before resolving the run.
   *
   * The weekday is resolved in UTC, shifted by the same day the UTC conversion
   * moves the time, exactly as buildSchedule writes it into the cron. That UTC
   * day is the one EventBridge fires on, and it lands back on the chosen weekday
   * in the subscription's zone. A weekDay pins the send to one day of the week,
   * so the result is that weekday's next occurrence at the configured time. Daily has
   * no weekDay: the result is today when the configured time is still ahead of
   * now, otherwise tomorrow.
   *
   * Returns null for inactive subscriptions, instant (post-loan) notifications,
   * and unparsable times, none of which have a scheduled next send.
   */
  getNextScheduledRun(subscription: any): Date | null {
    if (
      !subscription?.isActive ||
      subscription.notificationType === 'post-loan' ||
      subscription.timeInterval === 'instantly'
    ) {
      return null;
    }

    const utcTime = /^(\d{1,2}):(\d{2})$/.exec(
      this.notificationService.convertLocalToUtcTime(
        subscription.time || '',
        this.getSubscriptionTimeZone(subscription)
      )
    );
    if (!utcTime) {
      return null;
    }
    const utcHour = Number(utcTime[1]);
    const utcMinute = Number(utcTime[2]);

    // A daily cadence ignores weekDay, which can still hold a stale value from
    // a previously selected weekly cadence.
    const weekDay = subscription.timeInterval === 'daily' ? '' : subscription.weekDay;
    const localTargetDay = WEEK_DAY_INDEX[(weekDay || '').toLowerCase()];
    const hasTargetDay = localTargetDay !== undefined;
    const utcDayShift = this.notificationService.getUtcDayShift(
      subscription.time || '',
      this.getSubscriptionTimeZone(subscription)
    );
    const targetDay = hasTargetDay
      ? (((localTargetDay + utcDayShift) % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK
      : localTargetDay;

    const now = new Date();
    const buildRun = (dayOffset: number) => new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + dayOffset,
      utcHour,
      utcMinute,
      0,
      0
    ));

    const daysUntilTargetDay = hasTargetDay
      ? (targetDay - now.getUTCDay() + DAYS_PER_WEEK) % DAYS_PER_WEEK
      : 0;

    let nextRun = buildRun(daysUntilTargetDay);
    if (nextRun.getTime() <= now.getTime()) {
      // The send time has already passed: a weekly cadence waits a full week for
      // its weekday to come back around, a daily one waits until tomorrow.
      nextRun = buildRun(daysUntilTargetDay + (hasTargetDay ? DAYS_PER_WEEK : 1));
    }

    return nextRun;
  }

  /** Marks the current user's form as having unsaved changes. */
  markDirty(): void {
    this.formDirty = true;
  }

  /** Marks the managed user's form as having unsaved changes. */
  managedMarkDirty(): void {
    this.managedFormDirty = true;
  }

  /** Persists the current user's notification subscription settings. */
  async save() {
    // Prevents users from saving without completing the form
    const missingField = this.findMissingScheduleField(this.subscription);
    if (missingField) {
      this.snackBarService.openSnackBar(`Please select a ${missingField} before saving.`, 'Okay');
      return;
    }

    this.subscriptionFlag = true;
    this.subscription.isActive = this.subscriptionFlag;
    const isSaved = await this.saveSubscription(this.subscription, this.primaryEmail);
    // A failed save keeps the form open so the edits are not lost behind the summary.
    if (isSaved) {
      this.closeScheduleEdit();
    }
  }

  /** Persists the managed user's notification subscription settings. */
  async saveManagedUser() {
    // Prevents users from saving without completing the form
    const missingField = this.findMissingScheduleField(this.managedSubscription);
    if (missingField) {
      this.snackBarService.openSnackBar(`Please select a ${missingField} before saving.`, 'Okay');
      return;
    }

    // Snapshot so a failed save can roll the optimistic state back.
    const previousFlag = this.managedSubscriptionFlag;
    const previousIsActive = this.managedSubscription.isActive;

    this.managedSubscriptionFlag = true;
    this.managedSubscription.isActive = this.managedSubscriptionFlag;
    const isSaved = await this.saveSubscription(this.managedSubscription, this.managedPrimaryEmail);
    if (!isSaved) {
      // saveSubscription already surfaced the failure; just undo the optimistic
      // state and leave the form dirty so the user can retry.
      this.managedSubscriptionFlag = previousFlag;
      this.managedSubscription.isActive = previousIsActive;
      return;
    }

    this.managedFormDirty = false;
    if (this.managedRow) {
      this.savedRows.add(this.managedRow);
    }
  }

  /** Toggles the current user's subscription active state and persists immediately. */
  async onClickSwitch(event: MatSlideToggleChange) {
    // Prevents users from subscribing without completing the form
    const missingField = this.findMissingScheduleField(this.subscription);
    if (missingField) {
      this.revertToggle(event, this.subscriptionFlag);
      // The summary has nothing to complete in place, so opting in with an
      // unfinished schedule opens the form on the user's behalf. The form itself
      // shows what is missing, so no message is needed.
      if (!this.isEditingSchedule) {
        this.startScheduleEdit();
        return;
      }
      this.snackBarService.openSnackBar(`Please select a ${missingField} before opting in.`, 'Okay');
      return;
    }

    // Snapshot so a failed save can roll the optimistic toggle back.
    const previousFlag = this.subscriptionFlag;
    const previousIsActive = this.subscription.isActive;

    this.subscriptionFlag = event.checked;
    this.spinnerService.setShowSpinner(true);
    this.subscription.isActive = this.subscriptionFlag;
    const message = this.subscriptionFlag
      ? 'Subscribed to receive notifications'
      : 'Unsubscribed from notifications';
    const isSaved = await this.saveSubscription(this.subscription, this.primaryEmail);
    if (!isSaved) {
      // saveSubscription already surfaced the failure; undo the optimistic
      // toggle and skip the success message.
      this.subscriptionFlag = previousFlag;
      this.subscription.isActive = previousIsActive;
      this.revertToggle(event, previousFlag);
      return;
    }
    // When the toggle saves while the form is still open, the pre-edit snapshot
    // is now stale: a later Cancel would revert to it and wipe the just-saved
    // edits. Re-snapshot the persisted state so Cancel becomes a no-op.
    if (this.isEditingSchedule) {
      this.scheduleEditSnapshot = JSON.parse(JSON.stringify(this.subscription));
    }
    this.formDirty = false;
    this.snackBarService.openSnackBar(message, 'Okay');
  }

  /** Toggles the managed user's subscription active state and persists immediately. */
  async onClickSwitchManagedUserSub(event: MatSlideToggleChange) {
    // Prevents users from subscribing without completing the form
    const missingField = this.findMissingScheduleField(this.managedSubscription);
    if (missingField) {
      this.revertToggle(event, this.managedSubscriptionFlag);
      this.snackBarService.openSnackBar(`Please select a ${missingField} before opting in.`, 'Okay');
      return;
    }

    // Snapshot so a failed save can roll the optimistic toggle back.
    const previousFlag = this.managedSubscriptionFlag;
    const previousIsActive = this.managedSubscription.isActive;

    this.managedSubscriptionFlag = event.checked;
    this.spinnerService.setShowSpinner(true);
    this.managedSubscription.isActive = this.managedSubscriptionFlag;
    const message = this.managedSubscriptionFlag
      ? `Subscribed ${this.managedPrimaryEmail} to receive notifications`
      : `Unsubscribed ${this.managedPrimaryEmail} from notifications`;
    const isSaved = await this.saveSubscription(this.managedSubscription, this.managedPrimaryEmail);
    if (!isSaved) {
      // saveSubscription already surfaced the failure; undo the optimistic
      // toggle, leave savedRows untouched, and skip the success message.
      this.managedSubscriptionFlag = previousFlag;
      this.managedSubscription.isActive = previousIsActive;
      this.revertToggle(event, previousFlag);
      return;
    }

    // Reflect the saved state in the opted-in row markers only after success.
    if (this.managedRow && this.managedSubscriptionFlag) {
      this.savedRows.add(this.managedRow);
    } else if (this.managedSubscriptionFlag === false) {
      this.savedRows.delete(this.managedRow);
    }
    this.managedFormDirty = false;
    this.snackBarService.openSnackBar(message, 'Okay');
  }

  /** Closes the dialog without returning a result. */
  close(): void {
    this.dialogRef.close({ data: false });
  }

  /** Navigates into the managed-user detail view for the given user row. */
  async manageUser(row: any): Promise<void> {
    console.log('user to manage', row);
    this.managedRow = row;
    this.isAdminManagingUser = true;
    this.managedPrimaryEmail = row.username;
    await this.loadConfigsToManage(row.cognitoID);
  }

  /** Returns from the managed-user detail view back to the user list. */
  manageUserReturn(): void {
    this.isAdminManagingUser = false;
    this.managedFormDirty = false;
    this.managedSubscriptionFlag = false;
    this.managedSubscription = this.buildEmptySubscription();
    this.managedPrimaryEmail = '';
    this.managedRow = null;
  }

  // --- Private helpers ---

  /**
   * Reverts a slide-toggle back to the given model state after a rejected click.
   *
   * The toggle uses a one-way [checked] binding, so when validation rejects a
   * click the bound model never changes. A single assignment to
   * event.source.checked is skipped by Angular's change detection: it compares
   * each binding against the value it last wrote, not against the DOM, so it
   * considers the toggle "unchanged" and never flushes the correct state to the
   * native input. To force the write, we set the opposite state and flush, then
   * set the desired state and flush again, guaranteeing the value differs
   * between the two change-detection passes. The model itself is never touched.
   */
  private revertToggle(event: MatSlideToggleChange, state: boolean): void {
    event.source.checked = !state;
    this.changeDetectorRef.detectChanges();
    event.source.checked = state;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * The dropdown option a subscription has selected: its zone plus the
   * abbreviation it is pinned to. A config with no saved abbreviation matches
   * the one the zone is on now, which is the offset its stored time was saved
   * with. Undefined when the zone is not offered.
   */
  private findTimeZoneOption(subscription: any): NotificationTimeZoneOption | undefined {
    const timeZone = subscription?.timeZone;
    if (!timeZone) {
      return undefined;
    }
    const abbreviation =
      subscription.timeZoneAbbreviation ||
      this.notificationService.resolveDefaultTimeZoneAbbreviation(timeZone);

    return (
      this.timeZones.find(option => option.id === timeZone && option.abbreviation === abbreviation) ??
      this.timeZones.find(option => option.id === timeZone)
    );
  }

  /** The dropdown value for a subscription's time zone selection. */
  getTimeZoneSelection(subscription: any): string | null {
    return this.findTimeZoneOption(subscription)?.value ?? null;
  }

  /** Stores the picked option's zone and abbreviation on the subscription. */
  onTimeZoneSelected(subscription: any, value: string): void {
    const option = this.timeZones.find(timeZone => timeZone.value === value);
    if (!option) {
      return;
    }
    subscription.timeZone = option.id;
    subscription.timeZoneAbbreviation = option.abbreviation;
  }

  /**
   * The zone used to convert a subscription's times. For a daylight-saving
   * option that is a fixed-offset zone (UTC+10 for AEST), so the time keeps the
   * offset its label names all year.
   *
   * Falls back to the machine's own zone when nothing is stored, which keeps it
   * in step with the zone the dropdown defaults to for a new config.
   */
  private getSubscriptionTimeZone(subscription: any): string {
    return (
      this.findTimeZoneOption(subscription)?.conversionTimeZone ||
      subscription?.timeZone ||
      this.notificationService.resolveDefaultTimeZone()
    );
  }

  /**
   * Gives a loaded subscription a time zone and abbreviation when it was stored
   * before those fields existed, so the dropdown always has a selected option.
   * The abbreviation defaults to the one the zone is on now, which matches the
   * offset the stored time was converted with, so the displayed time is unchanged.
   */
  private backfillTimeZone(subscription: any): void {
    if (!subscription.timeZone) {
      subscription.timeZone = this.notificationService.resolveDefaultTimeZone();
    }
    if (!subscription.timeZoneAbbreviation) {
      subscription.timeZoneAbbreviation =
        this.notificationService.resolveDefaultTimeZoneAbbreviation(subscription.timeZone) ?? undefined;
    }
  }

  /** Creates a default subscription object with sensible initial values. */
  private buildEmptySubscription() {
    // No timeZoneAbbreviation here: findTimeZoneOption falls back to the zone's
    // current one, and saving fills it in.
    return {
      product: DEFAULT_PRODUCT_LABEL,
      timeInterval: '',
      weekDay: '',
      time: '13:00',
      timeZone: this.notificationService.resolveDefaultTimeZone(),
      filter: 'Fail',
      lastModified: '',
      emails: [] as string[],
      isActive: false
    };
  }

  /** Loads the current user's existing config and sets admin flag. */
  private async loadInitialData(): Promise<void> {
    await this.loadConfigsByCognitoID();
    this.primaryEmail = this.amplifyService.username;
    this.isAdmin = this.amplifyService.isAdmin;
    console.log('is admin?', this.isAdmin);
  }

  async loadSubscriptionEnrichments(): Promise<void> {
    // defaultValue guards against the catchError branch returning EMPTY, on
    // which firstValueFrom would otherwise reject and abort getExternalView
    // before it clears pleaseWait.
    const result = await firstValueFrom(
      this.notificationService
        .listConfigs(this.assetService.getSelectedAssetId())
        .pipe(
          takeUntil(this.unsubscribe),
          catchError(err => {
            console.error('Failed to load notification configs', err);
            this.snackBarService.openSnackBar('Failed to load notification settings.', 'Okay');
            this.spinnerService.setShowSpinner(false);
            return EMPTY;
          })
        ),
      { defaultValue: null }
    );
    console.log('Loaded configs by assetID', result);

    // A failed lookup surfaces two ways: null from the defaultValue above, or a
    // non-array HttpErrorResponse that the shared error handler emits as a value.
    // Either way, iterating would throw and abort getExternalView before it
    // clears pleaseWait, leaving the admin's user list stuck on its loading
    // message. Degrade to no enrichment instead: the table still renders, just
    // without the opted-in checkmarks.
    if (!Array.isArray(result)) {
      console.warn('[NotificationDialog] Skipping subscription enrichment, unexpected payload', result);
      return;
    }

    for (const enrichmentItem of result) {
      if (enrichmentItem.emails.length === 0) {
        continue;
      }
      const matchedUser = this.users.find((user: any) => 
        (user.username === enrichmentItem.emails[0]) && 
        (enrichmentItem.isActive === true));
      if (matchedUser) {
        this.savedRows.add(matchedUser);
      }
    }
  }

  /**
   * Fetches notification configs for the current user by their Cognito ID.
   * If no config exists, a new one is created server-side.
   */
  private async loadConfigsByCognitoID(): Promise<void> {
    // defaultValue guards against the catchError branch: it returns EMPTY, and
    // firstValueFrom rejects on an empty stream rather than resolving. Without
    // it, a load failure would reject out of ngOnInit and skip the spinner reset
    // below. The error toast and spinner are already handled in catchError.
    const result = await firstValueFrom(
      this.notificationService
        .listConfigs(this.assetService.getSelectedAssetId(), this.amplifyService.getCognitoId())
        .pipe(
          takeUntil(this.unsubscribe),
          catchError(err => {
            console.error('Failed to load notification configs', err);
            this.snackBarService.openSnackBar('Failed to load notification settings.', 'Okay');
            this.spinnerService.setShowSpinner(false);
            return EMPTY;
          })
        ),
      { defaultValue: null }
    );
    console.log('loadConfigsByCognitoID result', result);
    if (result === null) {
      return;
    }
    // Deep clone to avoid mutating the service response.
    const parsedResult = JSON.parse(JSON.stringify(result));

    if (parsedResult?.length === 1) {
      const notificationItem = parsedResult[0];
      this.subscription = notificationItem;
      this.subscriptionFlag = notificationItem.isActive;
      // The zone must be resolved before the conversion below, which reads it.
      this.backfillTimeZone(this.subscription);
      // Convert stored UTC time into the subscription's own zone for display.
      this.subscription.time = this.notificationService.convertUtcToLocalTime(
        this.subscription.time,
        this.getSubscriptionTimeZone(this.subscription)
      );
      this.primaryEmail = this.subscription.emails[0];
    } else if (parsedResult?.length === 0) {
      // No stored config yet: initialize an in-memory default only. It is
      // persisted when the user saves or opts in, never as a side effect of
      // opening the dialog.
      this.initNewConfig(this.amplifyService.getCognitoId());
    } else {
      console.log('[ERROR] multiple items found when there should only be one');
    }

    this.spinnerService.setShowSpinner(false);
  }

  /**
   * Fetches notification configs for a managed user by their Cognito ID.
   * Used by admins to view/edit another user's subscription settings.
   */
  private async loadConfigsToManage(cognitoId: string): Promise<void> {
    console.log('selected user cognitoId', cognitoId);
    const result = await firstValueFrom(
      this.notificationService
        .listConfigs(this.assetService.getSelectedAssetId(), cognitoId)
        .pipe(
          takeUntil(this.unsubscribe),
          catchError(err => {
            console.error('Failed to load managed user configs', err);
            this.snackBarService.openSnackBar('Failed to load user notification settings.', 'Okay');
            this.spinnerService.setShowSpinner(false);
            return EMPTY;
          })
        ),
      { defaultValue: null }
    );
    if (!result) {
      // The load failed. manageUser already switched into the managed-user view,
      // so leave it: an empty, uninitialized form must not be editable or
      // saveable. Return to the user list instead.
      this.isAdminManagingUser = false;
      return;
    }
    console.log('loadConfigsByCognitoID result', result);
    const parsedResult = JSON.parse(JSON.stringify(result));

    if (parsedResult?.length === 1) {
      const notificationItem = parsedResult[0];
      this.managedSubscription = notificationItem;
      this.managedSubscriptionFlag = notificationItem.isActive;
      // The zone must be resolved before the conversion below, which reads it.
      this.backfillTimeZone(this.managedSubscription);
      this.managedSubscription.time = this.notificationService.convertUtcToLocalTime(
        this.managedSubscription.time,
        this.getSubscriptionTimeZone(this.managedSubscription)
      );

      if (this.managedSubscription.emails[0]) {
        this.managedPrimaryEmail = this.managedSubscription.emails[0];
      }
    } else if (parsedResult?.length === 0) {
      // No stored config yet: initialize an in-memory default only (see below).
      // A default config is inactive, so clear any opted-in state carried over
      // from a previously managed user.
      this.managedSubscriptionFlag = false;
      this.initNewConfig(cognitoId);
    } else {
      console.log('[ERROR] multiple items found when there should only be one');
    }

    this.spinnerService.setShowSpinner(false);
  }

  /**
   * Initializes an in-memory default config for the given user without
   * persisting it.
   *
   * The item is written to DynamoDB only when the user saves or opts in via
   * saveSubscription. Building it here (rather than creating it server-side on
   * load) prevents an unwanted config item from being created merely by opening
   * the dialog or viewing a user, which previously produced duplicate items.
   */
  private initNewConfig(cognitoId: string): void {
    const newConfig = this.notificationService.buildNewConfig(
      this.assetService.getSelectedAssetId(),
      cognitoId,
      this.amplifyService.getCognitoId(),
      this.assetService.getSelectedFI()
    );
    // Mark as never-persisted so saveSubscription knows to use POST (create)
    // rather than PUT (update). This flag is cleared after a successful create.
    (newConfig as any)._isNew = true;
    if (this.isAdminManagingUser) {
      this.managedSubscription = newConfig;
    } else {
      this.subscription = newConfig;
    }
  }

  /**
   * Persists a notification subscription to the backend.
   * Collects email addresses, stamps last-modified, builds the cron schedule,
   * and converts local time back to UTC before saving.
   *
   * Routes to createNotification (POST) for new configs (version 0) or
   * updateNotification (PUT) for existing configs.
   *
   * @returns true when the backend accepted the save, false when it failed.
   */
  private async saveSubscription(subscription: any, primaryEmail: string): Promise<boolean> {
    this.spinnerService.setShowSpinner(true);

    // Deep clone to avoid mutating the bound model during transformation.
    const saveConfigs = JSON.parse(JSON.stringify(subscription));
    console.log('save item', saveConfigs);

    // Build the email list from the primary input.
    const emails: string[] = [];
    if (primaryEmail) {
      emails.push(primaryEmail);
    }
    saveConfigs.emails = emails;
    saveConfigs.lastModified = new Date().toISOString();

    // Derive the cron schedule and report window from the user's selected
    // time/interval, read in the subscription's own time zone.
    const { schedule, reportWindow } = this.notificationService.buildSchedule(
      saveConfigs.time,
      saveConfigs.timeInterval,
      saveConfigs.weekDay,
      this.getSubscriptionTimeZone(saveConfigs)
    );
    saveConfigs.schedule = schedule;
    saveConfigs.reportWindow = reportWindow;
    // Set notificationType based on the selected frequency.
    saveConfigs.notificationType = saveConfigs.timeInterval === 'instantly' ? 'post-loan' : 'scheduled';
    // Convert display time back to UTC for storage, from the same zone it was
    // displayed in, so a save round trip cannot shift the time.
    saveConfigs.time = this.notificationService.convertLocalToUtcTime(
      saveConfigs.time,
      this.getSubscriptionTimeZone(saveConfigs)
    );

    if (!saveConfigs?.company) {
      saveConfigs.company = this.assetService.getSelectedFI();
    }

    // Determine whether this is a new config (create) or an existing one (update).
    // _isNew is set by initNewConfig for configs that have never been persisted.
    // We cannot rely on version === 0 because the backend stores version as 0
    // and a subsequent session loading that config would incorrectly treat it as new.
    const isNewConfig = !!subscription._isNew;
    const save$ = isNewConfig
      ? this.notificationService.createNotification(saveConfigs)
      : this.notificationService.updateNotification(saveConfigs);

    return firstValueFrom(
      save$
        .pipe(
          takeUntil(this.unsubscribe),
          finalize(() => this.spinnerService.setShowSpinner(false)),
          catchError(err => {
            console.error('Failed to save notification config', err);
            this.snackBarService.openSnackBar('Failed to save notification settings', 'Okay');
            return EMPTY;
          })
        ),
      { defaultValue: null }
    ).then((response: any) => {
      // A failed save reaches here as null: catchError returns EMPTY, and the
      // defaultValue turns that empty stream into null rather than a rejection.
      // Report it as a failure so the caller keeps the form open.
      if (response == null) {
        return false;
      }
      // Keep the derived notificationType on the in-memory model in step with
      // what was persisted, so the summary and next-run display stay accurate
      // after a cadence change.
      subscription.notificationType = saveConfigs.notificationType;
      // After a successful create, clear the _isNew flag and update the in-memory
      // model so subsequent saves within the same session use PUT.
      if (isNewConfig) {
        subscription.notificationId = response.notificationId || saveConfigs.notificationId;
        subscription.version = response.version ?? subscription.version;
        delete subscription._isNew;
      } else if (response?.version != null) {
        subscription.version = response.version;
      }
      // Append the next run only when there is one. Instant, inactive, and
      // unconfigured subscriptions have no scheduled send, so they get the plain
      // message rather than a "next scheduled email: null" suffix. The run is
      // formatted in the subscription's own zone, matching the summary's row.
      const nextRunDisplay = this.getNextRunDisplay(subscription);
      const message = nextRunDisplay
        ? `Save Successful, next scheduled email: ${nextRunDisplay}`
        : 'Save Successful';
      this.snackBarService.openSnackBar(message, 'Okay');
      return true;
    });
  }

  /**
   * Fetches all users for the current account and populates the admin table.
   * Filters out internal NXTsoft, CCMC, and Kinective accounts so only
   * external client users are shown.
   */
  private async getExternalView(): Promise<void> {
    const assetUsers = await firstValueFrom(
      this.ccmcApiService
        .getUsersByAccountID(this.amplifyService.getAccountID())
        .pipe(
          takeUntil(this.unsubscribe),
          catchError(err => {
            console.error('Failed to load account users', err);
            this.snackBarService.openSnackBar('Failed to load users.', 'Okay');
            this.pleaseWait = false;
            return EMPTY;
          })
        ),
      { defaultValue: null }
    );
    if (!assetUsers) {
      return;
    }
    console.log("Asset users", assetUsers);
    this.assetUsers = assetUsers;
    // Exclude internal company emails and the signed-in user, who manages their
    // own subscription elsewhere, from the manageable user list.
    const currentUserCognitoId = this.amplifyService.getCognitoId();
    this.users = this.assetUsers.content.users.filter((obj: any) =>
      !obj.username.includes('@nxtsoft.com') &&
      !obj.username.includes('@ccmcinc.com') &&
      !obj.username.includes('kinective.io') &&
      obj.cognitoID !== currentUserCognitoId
    );

    // Finds which users are opted in to mark with a check
    await this.loadSubscriptionEnrichments();

    this.ccmcApiService.setUsers(this.users);
    this.dataSource = new MatTableDataSource(this.users);
    this.dataSource.sortingDataAccessor = (item: any, property: string) => {
      if (property === 'email') {
        return item.username?.toLowerCase() || '';
      }
      return item[property];
    };
    this.pleaseWait = false;

    // Defer sort assignment until the view child is available.
    setTimeout(() => {
      if (this.sort) {
        // Restrict sorting to ascending/descending only; never clear back to unsorted.
        this.sort.disableClear = true;
        this.sort.active = 'email';
        this.sort.direction = 'asc';
        this.dataSource.sort = this.sort;
      }
    });
  }

  /**
   * Determines whether the current user belongs to NXTsoft based on their
   * Amplify role flags. NXTsoft users see a different UI than external clients.
   */
  private getIfNxtsoft(): void {
    this.isUserNXTsoft = this.amplifyService.isNXTsoft === true;
    console.log(this.isUserNXTsoft);

    if (
      this.amplifyService.isNXTsoftDev ||
      this.amplifyService.isNXTsoftOperations ||
      this.amplifyService.isNXTsoftSales
    ) {
      this.isUserNXTsoft = true;
    }
    console.log('am I nxtsoft?', this.isUserNXTsoft);
  }
}
